/**
 * printPdf — öffnet eine Stellungnahme in einem neuen Browserfenster mit
 * Print-Friendly-CSS und triggert dort den Druck-Dialog. Die Konvertierung
 * zu PDF macht der Browser selbst (Druck-Dialog → „Als PDF speichern").
 *
 * Vorteil gegenüber DOCX→PDF Server-Roundtrip:
 *   - DSGVO: Mandantendaten verlassen das Endgerät NICHT.
 *   - Keine zusätzliche Infrastruktur (LibreOffice, Cloud-API, Worker).
 *   - Browser-Print-Engine ist ausgereift und liefert hochwertige PDFs.
 *
 * Browser-Hinweis: Pop-up-Blocker sperren `window.open` standardmäßig wenn
 * es nicht aus einem User-Click-Handler kommt. Wir geben deshalb eine klare
 * Fehlermeldung zurück, falls das Window nicht geöffnet werden konnte —
 * der UI-Layer kann dem Berater dann sagen, Pop-ups für die Pilot-Domain
 * zuzulassen.
 *
 * @see ./renderHtml.ts (HTML-Renderer)
 * @see ./renderDocx.ts (paralleler DOCX-Pfad bleibt bestehen)
 */

import type { statement } from '@tragfaehigkeit/core';
import { renderStatementHtml } from './renderHtml';

type StatementPlaceholders = statement.StatementPlaceholders;

export interface OpenPrintPreviewResult {
  readonly status: 'opened' | 'blocked';
  /** Erklärungstext für den UI-Layer wenn der Pop-up-Blocker zugeschlagen hat. */
  readonly hint?: string;
}

/**
 * Öffnet die Stellungnahme als druckbare HTML-Vorschau in einem neuen
 * Browser-Tab. Triggert den Print-Dialog automatisch nach `load`.
 *
 * Aufruf nur aus einem User-Click-Handler (sonst Pop-up-Block).
 */
export function openPrintPreview(placeholders: StatementPlaceholders): OpenPrintPreviewResult {
  const html = renderStatementHtml(placeholders);

  // Wir nutzen Blob+Object-URL statt document.write, weil document.write
  // in modernen Browsern als Anti-Pattern gilt und in Firefox/Chrome
  // CSP-Probleme bringt. Der Blob bekommt MIME text/html, der Browser
  // rendert ihn wie eine normale Seite.
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (win === null) {
    URL.revokeObjectURL(url);
    return {
      status: 'blocked',
      hint:
        'Der Browser hat das Vorschau-Fenster blockiert (Pop-up-Blocker). ' +
        'Bitte erlauben Sie Pop-ups für diese Seite und versuchen Sie es erneut.',
    };
  }

  // Object-URL nach kurzer Verzögerung freigeben — das Fenster hat dann
  // den Inhalt schon übernommen. Print-Dialog ruft das neue Fenster
  // selbst über die kleine Toolbar im HTML auf.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);

  return { status: 'opened' };
}
